import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import useTranslation from 'next-translate/useTranslation';
import { css } from '@emotion/react';
import { CoinColorIcon, BellIcon, AdultLabelIcon } from 'public/assets';
import theme from '@styles/theme';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import {
  setIsRestartingForRemainingAfterClear,
  setWillUseCoin,
} from '@/slices/chatChapterSlice';
import { RootState } from '@store/store';
import { CHAPTER_PURCHASES } from '@api/story';
import {
  formatWaitingTime,
  operationDate,
  isMobileDevice,
} from '@common/utils';
import { ChapterPurchasesQuery } from '@customTypes/story';
import { Chapter } from '@customTypes/chapter';
import {
  viewPurchaseStory,
  touchPurchaseStory,
  touchOpenAppNextChapter,
  touchAllChapterPurchase,
  touchTicketForNextChapter,
} from '@lib/eventManager';
import useCheckAd from '@/hooks/useCheckAdByRemoteConfig';
import { GetUser } from '@customTypes/user';
import { GET_USER } from '@api/user';

import { Button, SPLoading, SPImage } from '@components/commons';
import StoryDetailButton from '@components/storyDetail/button/Button';
import useCheckAdultCertification from '@/hooks/useCheckAdultCertification';
import WholePurchaseInfoPopup from '@/components/commons/popup/WholePurchaseInfoPopup';
import { setDeeplink } from '@/lib/airbridge';
import { goLoginForApp, goStoreForApp } from '@/lib/webview';
import StoryGameWholePurchaseInfoPopup from '@/components/commons/popup/wholePurchasePopup/StoryGameWholePurchaseInfoPopup';

interface INextChapter {
  storyImage?: string;
  nextChapter?: Chapter;
  charName?: string;
  waitingTime?: number;
  reset?: () => void;
  price?: number;
  discountRate?: number;
  isShowAd?: boolean;
  storyId?: number;
  storyName?: string;
  originalPrice?: number;
  lastPreviewChapterIndex?: number;
  userData?: GetUser;
  hasAdultChapter?: boolean;
  contentType?: 'interactive' | 'story';
  coinProduct?: {
    inappProductId: string;
    inappCoinId: number;
    productName: string;
    amount: number;
    originalPrice: number;
    originalPriceFloat?: number | null;
    price: number;
    priceFloat: number;
    discountRateForFloat: number;
    countryCode?: string | null;
    countryCodeV3?: string | null;
    currency?: string | null;
    tag?: {
      __typename?: 'InappCoinTag';
      text: string;
      backgroundColor: string;
    } | null;
  };
}

const WholePurchase = ({
  storyImage,
  nextChapter,
  price,
  originalPrice,
  storyId,
  storyName,
  lastPreviewChapterIndex,
  hasAdultChapter,
  contentType,
  coinProduct,
}: INextChapter) => {
  const { t } = useTranslation();
  const [isWholePurchasePopupOpen, setIsWholePurchasePopupOpen] =
    useState<boolean>(false);
  const { showAdultChapterIcon } = useCheckAdultCertification({
    storyId: nextChapter?.storyId,
    chapterId: nextChapter?.chapterId,
  });

  useEffect(() => {
    viewPurchaseStory({
      storyName,
      storyId,
      fullPrice: originalPrice,
      price,
      lastPreviewChapterIndex,
      from: 'chat',
    });
  }, []);

  const handlePurchaseStoryButtonClick = () => {
    setIsWholePurchasePopupOpen(true);
    touchPurchaseStory({
      storyName,
      storyId,
      fullPrice: originalPrice,
      price,
      lastPreviewChapterIndex,
      from: 'chat',
    });
    touchAllChapterPurchase({
      storyName,
      storyId: String(storyId),
      fullCoin: originalPrice,
      coin: price,
      lastPreviewChapterIndex,
      contentType: 'interactive',
    });
  };

  return (
    <>
      <div className="chapter">
        <div className="thumbnail">
          {storyImage && (
            <SPImage
              className="thumbnail"
              src={storyImage}
              layout="fixed"
              width={96}
              height={54}
              alt={'profile'}
              objectFit={'cover'}
            />
          )}
        </div>
        <div className="info">
          <div className="next">
            {t('chapterending_screen_title_next_chapter')}
          </div>
          <div className="title">
            {showAdultChapterIcon && (
              <AdultLabelIcon width={16} height={16} className="adult-icon" />
            )}
            <p>{nextChapter?.name}</p>
          </div>
        </div>
      </div>
      <div className="button">
        <StoryDetailButton
          isFill={true}
          position="center"
          onClick={handlePurchaseStoryButtonClick}
        >
          {contentType === 'story' && coinProduct ? (
            <>
              <p className="whole-purchase-button-text">
                {t('storygame_detail_button_purchase_unlimited')}
              </p>
            </>
          ) : (
            <p className="whole-purchase-button-text">
              {t('common_screen_button_play_after_purchase')}
              <CoinColorIcon />
              {price}
            </p>
          )}
        </StoryDetailButton>
      </div>
      {nextChapter &&
        (contentType === 'story' ? (
          <StoryGameWholePurchaseInfoPopup
            storyName={nextChapter.story.name}
            originalCoin={nextChapter.story.originalPrice}
            storyCoin={price ?? 0}
            isShow={isWholePurchasePopupOpen}
            storyId={storyId}
            chapterIndex={nextChapter.chapterIndex}
            onClose={() => setIsWholePurchasePopupOpen(false)}
            storyBundleId={nextChapter.story.bundleId ?? undefined}
            storyLanguage={nextChapter.story.languageCode ?? undefined}
            storyGenre={nextChapter.story.genreKey?.type}
            playType={nextChapter.story.playType}
            coverImage={nextChapter.story.mainImageFile?.link}
            discountRate={nextChapter.story.discountRate}
            hasAdultChapter={!!hasAdultChapter}
            coinProduct={coinProduct}
          />
        ) : (
          <WholePurchaseInfoPopup
            storyName={nextChapter.story.name}
            originalCoin={nextChapter.story.originalPrice}
            storyCoin={price ?? 0}
            isShow={isWholePurchasePopupOpen}
            storyId={storyId}
            chapterIndex={nextChapter.chapterIndex}
            onClose={() => setIsWholePurchasePopupOpen(false)}
            storyBundleId={nextChapter.story.bundleId ?? undefined}
            storyLanguage={nextChapter.story.languageCode ?? undefined}
            storyGenre={nextChapter.story.genreKey?.type}
            playType={nextChapter.story.playType}
            coverImage={nextChapter.story.mainImageFile?.link}
            discountRate={nextChapter.story.discountRate}
            hasAdultChapter={!!hasAdultChapter}
          />
        ))}
    </>
  );
};

const Purchase = ({
  storyImage,
  nextChapter,
  price,
  originalPrice,
  storyId,
  storyName,
  lastPreviewChapterIndex,
  reset,
  contentType,
}: INextChapter) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { showAdultChapterIcon, goToAdultChapter } = useCheckAdultCertification(
    {
      storyId,
      chapterId: nextChapter?.chapterId,
    },
  );

  useEffect(() => {
    viewPurchaseStory({
      storyName,
      storyId,
      fullPrice: originalPrice,
      price,
      lastPreviewChapterIndex,
      from: 'chat',
    });
  }, []);

  const handlePurchaseStoryButtonClick = () => {
    dispatch(setIsRestartingForRemainingAfterClear(false));

    reset && reset();
    touchPurchaseStory({
      storyName,
      storyId,
      fullPrice: originalPrice,
      price,
      lastPreviewChapterIndex,
      from: 'chat',
    });

    touchTicketForNextChapter({
      storyName,
      storyId: String(storyId),
      chapterId: router.query.chapterId as string,
      chapterIndex: nextChapter?.chapterIndex,
      coin: price,
      contentType: 'interactive',
    });

    delete router.query.chapterId;

    nextChapter &&
      goToAdultChapter({
        storyId: nextChapter.storyId,
        chapterId: nextChapter.chapterId,
        query: {
          prevScreen: contentType == 'story' ? 'story' : 'chat',
          backUrl: `/story/${storyId}`,
          chapterIndex: nextChapter.chapterIndex,
        },
        price: price,
      });
  };

  return (
    <>
      <div className="chapter">
        <div className="thumbnail">
          {storyImage && (
            <SPImage
              className="thumbnail"
              src={storyImage}
              layout="fixed"
              width={96}
              height={54}
              alt={'profile'}
              objectFit={'cover'}
            />
          )}
        </div>
        <div className="info">
          <div className="next">
            {t('chapterending_screen_title_next_chapter')}
          </div>
          <div className="title">
            {showAdultChapterIcon && (
              <AdultLabelIcon width={16} height={16} className="adult-icon" />
            )}
            <p>{nextChapter?.name}</p>
          </div>
        </div>
      </div>
      <div className="button">
        <StoryDetailButton
          isFill={true}
          position="center"
          onClick={handlePurchaseStoryButtonClick}
        >
          <p className="whole-purchase-button-text">
            {t('common_screen_button_purchase')}
            <CoinColorIcon />
            {price}
          </p>
        </StoryDetailButton>
      </div>
    </>
  );
};

const Free = ({
  storyImage,
  nextChapter,
  reset,
  isShowAd,
  contentType,
}: INextChapter) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { featureVersion } = useAppSelector((state: RootState) => state.user);
  const { showAdultChapterIcon, goToAdultChapter } = useCheckAdultCertification(
    {
      storyId: nextChapter?.storyId,
      chapterId: nextChapter?.chapterId,
    },
  );

  useEffect(() => {
    nextChapter &&
      setTimeout(() => {
        setDeeplink({ storyId: nextChapter?.storyId });
      });
  }, [nextChapter]);

  const handleNextChapterClick = () => {
    dispatch(setIsRestartingForRemainingAfterClear(false));
    reset && reset();
    delete router.query.chapterId;

    nextChapter &&
      goToAdultChapter({
        storyId: nextChapter.storyId,
        chapterId: nextChapter.chapterId,
        query: {
          prevScreen: contentType == 'story' ? 'story' : 'chat',
          backUrl: `/story/${nextChapter.storyId}`,
          startType: 'next',
          adsOn: isShowAd,
          chapterIndex: nextChapter.chapterIndex,
        },
      });
  };

  const handleOpenAppButtonClick = () => {
    touchOpenAppNextChapter({
      storyId: nextChapter?.storyId,
      storyName: nextChapter?.story.name,
      chapterId: Number(router.query.chapterId),
    });

    nextChapter && setDeeplink({ storyId: nextChapter?.storyId });
  };

  return (
    <>
      <div className="chapter">
        <div className="thumbnail">
          {storyImage && (
            <SPImage
              className="thumbnail"
              src={storyImage}
              layout="fixed"
              width={96}
              height={54}
              alt={'profile'}
              objectFit={'cover'}
            />
          )}
        </div>
        <div className="info">
          <div className="next">
            {t('chapterending_screen_title_next_chapter')}
          </div>
          <div className="title-wrapper">
            <div className="title">
              {showAdultChapterIcon && (
                <AdultLabelIcon width={16} height={16} className="adult-icon" />
              )}
              <p>{nextChapter?.name}</p>
            </div>
            {isShowAd && <div className="ad-icon">AD</div>}
          </div>
        </div>
      </div>
      <div className="button">
        <Button
          customCSS={buttonStyle}
          title={t('chapterending_screen_button_show_next_chapter')}
          onClick={handleNextChapterClick}
        />
        {featureVersion === 1 && !isMobileDevice() && (
          <Button
            customCSS={secondButtonStyle({
              isStoryGame: contentType === 'story',
            })}
            title={t('chat_toast_button_view_in_app')}
            isFill={false}
            onClick={handleOpenAppButtonClick}
            id={'web-to-app-deeplink'}
          />
        )}
      </div>
    </>
  );
};

const Waiting = ({
  storyImage,
  nextChapter,
  price,
  originalPrice,
  storyId,
  storyName,
  lastPreviewChapterIndex,
  reset,
  userData,
  contentType,
}: INextChapter) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const router = useRouter();

  const handleGoNextChapterButtonClick = () => {
    dispatch(setIsRestartingForRemainingAfterClear(false));
    reset && reset();

    if (userData?.getUser.isAnonymous) {
      isMobileDevice()
        ? goLoginForApp()
        : router.replace({
            pathname: '/auth',
            query: {
              url: `/story/${storyId}`,
            },
          });
      return;
    }

    if (price && userData && userData.getUser.currentCoin < price) {
      isMobileDevice()
        ? goStoreForApp()
        : router.replace({
            pathname: '/store',
            query: {
              coin: price,
              url: `/story/${storyId}`,
            },
          });
      return;
    }

    dispatch(setWillUseCoin(true));
    delete router.query.chapterId;
    const pathName = contentType === 'story' ? 'storygame' : 'chatstory';
    const screenName = contentType === 'story' ? 'story' : 'chat';
    router.replace({
      pathname: `/${pathName}/${nextChapter?.chapterId}`,
      query: {
        prevScreen: screenName,
      },
    });
    touchPurchaseStory({
      storyName,
      storyId,
      fullPrice: originalPrice,
      price,
      lastPreviewChapterIndex,
      from: 'chat',
    });
  };

  return (
    <>
      <div className="chapter waiting">
        <div className="left">
          <div className="thumbnail">
            {storyImage && (
              <SPImage
                className="thumbnail"
                src={storyImage}
                layout="fixed"
                width={96}
                height={54}
                alt={'profile'}
                objectFit={'cover'}
              />
            )}
          </div>
          <div className="info">
            <div className="next">
              {t('chapterending_screen_title_next_chapter')}
            </div>
            <div className="title">{nextChapter?.name}</div>
          </div>
        </div>
        <div>
          <div className="price">
            <CoinColorIcon />
            <div>{nextChapter?.price || 2}</div>
          </div>
        </div>
      </div>
      <div className="free-content">
        {!!nextChapter?.willFreeAt &&
          !!nextChapter?.remainingTimeToFree &&
          formatWaitingTime({
            seconds: nextChapter?.remainingTimeToFree,
            t,
            isSentence: true,
          })}
      </div>
      <div className="button waiting">
        <Button
          customCSS={buttonStyle}
          title={t('chapterending_screen_button_show_next_chapter')}
          onClick={handleGoNextChapterButtonClick}
        />
      </div>
    </>
  );
};

const Notice = ({ storyImage, charName }: INextChapter) => {
  const { t } = useTranslation();
  return (
    <>
      <div className="chapter">
        <div className="thumbnail">
          {storyImage && (
            <div className="bell-container">
              <SPImage
                className="thumbnail"
                src={storyImage}
                layout="fixed"
                width={96}
                height={54}
                alt={'profile'}
                objectFit={'cover'}
              />
              <div className="bell-bg"></div>
              <div className="bell">
                <BellIcon width={24} height={24} />
              </div>
            </div>
          )}
        </div>
        <div className="info">
          <div className="notice-title">
            {t('chat_popup_title_thank_you_for_playing')}
          </div>
          <div className="notice-content notice-content-name">
            {t('chat_popup_title_continue_your_story', { value: charName })}
          </div>
        </div>
      </div>
      <div className="button">
        <Button
          isDisable={true}
          customCSS={buttonStyle}
          title={t('storydetail_screen_title_wait_fot_next_chapter')}
        />
      </div>
    </>
  );
};

const NextChapter = ({
  storyImage,
  nextChapter,
  charName,
  reset,
  contentType,
}: INextChapter) => {
  const { isShowAdIcon } = useCheckAd({
    storyId: nextChapter?.storyId,
    chapterId: nextChapter?.chapterId,
  });
  const { data: userData } = useQuery<GetUser>(GET_USER);
  const [conditions, setConditions] = useState<{
    isFreeChapter?: boolean;
    needPurchaseWhole?: boolean;
    isWaitingTimeRemaining?: boolean;
  } | null>(null);
  const isStoryGame = useRouter().pathname.includes('storygame');
  const { data, loading } = useQuery(CHAPTER_PURCHASES, {
    variables: {
      data: { id: Number(nextChapter?.storyId) },
    },
    skip: isNaN(Number(nextChapter?.storyId)),
    onCompleted: (data) => {
      const storyDetail: ChapterPurchasesQuery = data.story;
      const isFree =
        nextChapter?.freedAt !== null ||
        nextChapter.price === 0 ||
        nextChapter.userPurchasedChapter !== null; // 전체구매했나?
      // 기스무 시간이 지났나?
      const isWaitingTimeZero =
        nextChapter?.freeDependencyChapterIndex !== null &&
        nextChapter?.freeDependencyPeriod !== null &&
        nextChapter?.remainingTimeToFree === 0 &&
        isFree;
      // 다음 회차가 기스무이고, 시간이 남았나?
      // 또는 다음 회차가 기간 후 무료 회차이고, 시간이 남았나?
      const isWaitingTimeRemaining =
        (nextChapter?.freeDependencyChapterIndex !== null &&
          nextChapter?.freeDependencyPeriod !== null &&
          nextChapter?.remainingTimeToFree !== 0) ||
        (nextChapter?.willFreeAt !== null &&
          nextChapter?.remainingTimeToFree !== null);
      // 전체구매했나? 이 필드는 이제 안쓰이는건가?
      const purchasedChapterIds = storyDetail?.chapterPurchases
        .filter((chapter) => !chapter.refundedAt)
        .map((chapter) => chapter.chapterId);
      // 통구매 작품이고, 아직 구매 안했나?
      const isWholePurchase =
        storyDetail?.purchasable && !storyDetail?.purchased;
      // nextChapterIndex가 lastPreviewChapterIndex보다 큰가?
      const isBiggerThenLastPreviewIndex =
        nextChapter &&
        storyDetail?.lastPreviewChapterIndex &&
        nextChapter.chapterIndex > storyDetail?.lastPreviewChapterIndex;

      const isPurchased =
        purchasedChapterIds &&
        nextChapter &&
        purchasedChapterIds.indexOf(nextChapter.chapterId) !== -1;

      const isPublished = operationDate({
        operation: '>=',
        comparisonDateString: nextChapter?.publishedAt,
      });

      const isFreeChapter =
        isPublished && (isFree || isWaitingTimeZero || isPurchased);

      const needPurchaseWhole = Boolean(
        isWholePurchase && isBiggerThenLastPreviewIndex,
      );

      setConditions({
        isFreeChapter,
        needPurchaseWhole,
        isWaitingTimeRemaining,
      });
    },
  });

  if (loading) {
    return <SPLoading />;
  }

  return (
    <div css={container({ isStoryGame })}>
      <div className="content">
        {!nextChapter ? (
          <Notice
            storyImage={storyImage}
            nextChapter={nextChapter}
            charName={charName}
          />
        ) : conditions?.isFreeChapter ? (
          <>
            {conditions?.needPurchaseWhole ? (
              <WholePurchase
                storyImage={storyImage}
                nextChapter={nextChapter}
                price={data.story.price}
                storyId={data.story.storyId}
                storyName={data.story.name}
                originalPrice={data.story.originalPrice}
                lastPreviewChapterIndex={data.story.lastPreviewChapterIndex}
                hasAdultChapter={data.story.hasAdultChapter}
                contentType={contentType}
                coinProduct={data.story.coinProduct}
              />
            ) : (
              <Free
                storyImage={storyImage}
                nextChapter={nextChapter}
                reset={reset}
                isShowAd={isShowAdIcon}
                contentType={contentType}
              />
            )}
          </>
        ) : conditions?.isWaitingTimeRemaining ? (
          <Waiting
            storyImage={storyImage}
            nextChapter={nextChapter}
            price={nextChapter?.price}
            storyId={data.story.storyId}
            storyName={data.story.name}
            originalPrice={data.story.originalPrice}
            lastPreviewChapterIndex={data.story.lastPreviewChapterIndex}
            reset={reset}
            userData={userData}
            contentType={contentType}
          />
        ) : (
          <Purchase
            storyImage={storyImage}
            nextChapter={nextChapter}
            price={nextChapter?.price}
            storyId={data.story.storyId}
            storyName={data.story.name}
            originalPrice={data.story.originalPrice}
            lastPreviewChapterIndex={data.story.lastPreviewChapterIndex}
            reset={reset}
            contentType={contentType}
          />
        )}
      </div>
    </div>
  );
};

const container = ({ isStoryGame }: { isStoryGame: boolean }) => css`
  width: 100%;
  max-width: 800px;
  height: 100%;
  overflow-y: scroll;
  display: grid;
  align-items: flex-end;
  padding: ${isStoryGame ? '1rem' : ' 1.25rem'};
  z-index: 10;

  ${isStoryGame
    ? theme.setTabletStyle(css`
        padding: 0;
        max-width: none;
      `)
    : theme.setMobileStyle(css`
        padding: 0.8rem;
      `)}

  ::-webkit-scrollbar {
    display: none;
  }

  .content {
    width: 100%;
    border-radius: 0.5em;
    background-color: ${theme.colors.white};
  }

  .chapter {
    padding: 20px 20px 0 20px;
    display: flex;
  }

  .thumbnail {
    width: 6rem;
    max-height: 3.375rem;
    box-sizing: border-box;
    border-radius: 0.25rem;
    background-color: gray;
    object-fit: cover;
  }

  .info {
    width: 100%;
    padding: 10px 0;
    gap: 2px;
    display: flex;
    flex-direction: column;
    margin-left: 16px;
  }

  .title-wrapper {
    display: flex;
    justify-content: space-between;
  }

  .ad-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.5625rem;
    font-weight: 800;
    color: ${theme.colors.gray500};
    border: 0.125rem solid ${theme.colors.gray400};
    border-radius: 0.125rem;
    padding: 0.1563rem 0.25rem 0.0625rem;
    width: 1.5rem;
    height: 1.125rem;
  }

  .next {
    font-size: 12px;
    font-weight: 400;
    color: ${theme.colors.primary500};
  }

  .title {
    font-size: 1em;
    font-weight: 700;
    color: ${theme.colors.gray900};
    display: flex;
    align-items: flex-start;
    gap: 0.25rem;

    .adult-icon {
      margin-top: 0.0625rem;
      flex-shrink: 0;
    }
  }

  .button {
    padding: 20px;
    display: flex;
    gap: 10px;
    ${theme.fontV3.body.body03Bold};

    .whole-purchase-button-text {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;
    }
  }

  .button.waiting {
    padding: 0 20px 20px;
  }

  .ad {
    font-family: AppleSDGothicNeo;
    color: ${theme.colors.gray600};
    margin-top: 12px;
  }

  .free-content {
    margin: 0.1em 1.25em 0 1.25em;
    padding: 1em 0;
    font-size: 14px;
    color: ${theme.colors.gray600};
  }

  .price {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 14px;
    font-weight: 700;
    color: ${theme.colors.point500};
  }

  .bell-container {
    width: 100%;
    position: relative;
  }

  .bell-bg {
    width: 100%;
    height: 4.625rem;
    position: absolute;
    left: 0;
    top: 0;
    background-color: ${theme.colors.gray900};
    opacity: 0.6;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.25rem;
  }

  .bell {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .notice-title {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
    /* identical to box height, or 150% */

    letter-spacing: -0.3px;

    /* Gray/Gray 900 (#242526) */

    color: #242526;
  }

  .notice-content {
    font-family: Apple SD Gothic Neo;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 20px;
    /* or 143% */

    letter-spacing: -0.3px;

    /* Gray/Gray 600 (#7E8185) */

    color: #7e8185;
  }

  .notice-content-name {
    margin-top: 10px;
  }

  .check {
    position: absolute;
    left: 20.83%;
    right: 29.17%;
    top: 47.86%;
    bottom: 24.02%;

    /* Gray/Gray 500 (#A4A7AD) */

    border: 2px solid #a4a7ad;
    transform: rotate(-45deg);
  }

  .chapter.waiting {
    justify-content: space-between;

    .left {
      display: flex;
    }
  }
`;

const buttonStyle = css`
  width: 100%;
  height: 3em;
  font-size: 1em;
  font-weight: 700;
  cursor: pointer;
`;

const secondButtonStyle = ({ isStoryGame }: { isStoryGame: boolean }) => css`
  ${isStoryGame && `display: none;`}
  width: 100%;
  height: 3em;
  font-size: 1em;
  font-weight: 700;
  cursor: pointer;
  background-color: ${theme.colors.white};
  color: ${theme.colors.primary500};
  border: 0.0625em solid ${theme.colors.primary300};
  border-radius: 6px;

  ${theme.setMobileStyle(css`
    ${isStoryGame && `display: block;`}
  `)}
`;

export default NextChapter;
